import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import color from '@deprecated-ui/_style/color';
import { formatPercentage, roundNumber } from 'utils/number';
import DayTradeBadge from 'features/day-trade/components/Badge';
import If from 'global/Common/If';
import {
  addPlusMinusIndicator,
  addPrefixAfterPlusMinusNotation,
  formatDate,
  formatPrice,
  formatPriceRoundDown,
  formatCeilToDecimals,
  getRealizedPnLText,
  renderTableRowContent,
} from './utils';
import { SellBadge } from './styled';
import {
  FR_BONDS,
  HistoryType,
  BONDS,
  COUPONS,
  REDEMPTIONS,
  TRANSFERS,
} from './types';

export const MounthlySeparator = ({
  groupDate,
  groupRealizedGain,
}: {
  groupDate: string;
  groupRealizedGain?: number | null;
}) => {
  const isRealizedGainHasValueNumber = typeof groupRealizedGain === 'number';

  let colorIndicator = '#858585';

  if (isRealizedGainHasValueNumber) {
    if (groupRealizedGain > 0) {
      colorIndicator = color.primary;
    }
    if (groupRealizedGain < 0) {
      colorIndicator = color.sunsetOrange;
    }
  }

  const monthlyRealizedAmount = roundNumber(groupRealizedGain, '0,0');

  return (
    <Flex justify="space-between" align="center">
      <Text size="14px" family="medium" color={color.highgray}>
        {groupDate}
      </Text>

      {isRealizedGainHasValueNumber && (
        <Text size="14px" family="medium" color={colorIndicator}>
          {`${addPlusMinusIndicator(
            monthlyRealizedAmount,
          )}${addPrefixAfterPlusMinusNotation(monthlyRealizedAmount)}`}
        </Text>
      )}
    </Flex>
  );
};

export const middleColumns = [
  {
    key: 'lot',
    title: 'Lot Done',
    dataIndex: 'lot',
    width: '14%',
    render: (lot, dataObject) => {
      const isRedemption = REDEMPTIONS.includes(dataObject.command);
      const isBonds = FR_BONDS.includes(dataObject.command);

      if (isBonds) {
        return renderTableRowContent(
          lot,
          dataObject,
          <Text align="right">{dataObject.shares}</Text>,
        );
      }

      return renderTableRowContent(
        lot,
        dataObject,
        <Text align="right">{lot === 0 || isRedemption ? '-' : lot}</Text>,
      );
    },
  },
  {
    key: 'price',
    title: 'Price',
    dataIndex: 'price',
    className: 'td-mobile history-price',
    width: '13%',
    render: (text, dataObject) => {
      const isBondsFR = FR_BONDS.includes(dataObject.command);
      const isRedemption = REDEMPTIONS.includes(dataObject.command);
      const isCoupon = COUPONS.includes(dataObject.command);

      if (isCoupon) {
        return renderTableRowContent(
          text,
          dataObject,
          <Text align="right">-</Text>,
        );
      }

      if (isRedemption) {
        return renderTableRowContent(
          text,
          dataObject,
          <Text align="right">100.00%</Text>,
        );
      }

      return renderTableRowContent(
        text,
        dataObject,
        <Text align="right">
          {isBondsFR ? formatPercentage(text) : formatPriceRoundDown(text)}
        </Text>,
      );
    },
  },
  {
    key: 'amount',
    title: 'Amount Done',
    dataIndex: 'amount',
    className: 'td-mobile history-amount-done',
    width: '15%',
    render: (text, dataObject) => {
      const isTransfer = TRANSFERS.includes(dataObject.command);

      if (isTransfer) {
        return renderTableRowContent(
          text,
          dataObject,
          <Text align="right">-</Text>,
        );
      }

      return renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatPrice(text)}</Text>,
      );
    },
  },
  {
    key: 'fee',
    title: 'Total Fee',
    dataIndex: 'fee',
    width: '15%',
    render: (text, dataObject) => {
      // isSellFR, isCouponFR, isCouponKAEF01CB, isCouponSBN
      const couponCommands = [
        HistoryType.SELL_FR,
        HistoryType.COUPON_FR,
        HistoryType.COUPON_KAEF01CB,
        HistoryType.COUPON_SBN,
      ];
      const showMinusSign = couponCommands.includes(dataObject.command);

      return renderTableRowContent(
        text,
        dataObject,
        <Text align="right">
          {showMinusSign && text !== 0 && '-'}
          {formatPrice(text, !showMinusSign)}
        </Text>,
      );
    },
  },
  {
    key: 'netamount',
    title: 'Net Amount',
    dataIndex: 'netamount',
    width: '15%',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">
          {dataObject.command === HistoryType.LATE_FEE
            ? formatCeilToDecimals(text, '-')
            : formatPrice(text)}
        </Text>,
      ),
  },
];

export const middleColumnsBonds = [
  {
    key: 'price',
    title: 'Price',
    dataIndex: 'price',
    className: 'td-mobile history-price',
    width: '13%',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatPercentage(text)}</Text>,
      ),
  },
  {
    key: 'netamount',
    title: 'Total Amount',
    dataIndex: 'netamount',
    className: 'td-mobile history-amount-done',
    width: '15%',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatPrice(text)}</Text>,
      ),
  },
];

export const allColumns = [
  {
    key: 'display_as',
    title: 'Ordered Items',
    dataIndex: 'display_as',
    className: 'td-mobile history-order-item',
    width: '13%',
    render: (text, dataObject) => {
      const isShowForcellBadge =
        dataObject.is_day_trade &&
        dataObject.command === HistoryType.FORCED_SELL_DAY_TRADE;

      if (dataObject.groupDate) {
        return {
          children: <MounthlySeparator groupDate={dataObject.groupDate} />,
          props: { colSpan: 7 },
        };
      }

      return (
        <Flex align="center">
          <Text color={dataObject.status_color_code} as="span" family="bold">
            {text}{' '}
          </Text>
          <If condition={dataObject.label}>
            <SellBadge family="bold">
              {dataObject.label.toUpperCase()}
            </SellBadge>
          </If>
          <If condition={isShowForcellBadge}>
            <DayTradeBadge label="FORCED SELL" isDayTrade />
          </If>
        </Flex>
      );
    },
  },
  ...middleColumns,
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    className: 'td-mobile history-date normal',
    width: '15%',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatDate(text)}</Text>,
      ),
  },
];

export const realizedColumns = [
  {
    key: 'symbol',
    title: 'Ordered Items',
    dataIndex: 'symbol',
    className: 'td-mobile history-order-item realized',
    render: (text, dataObject) => {
      if (dataObject.groupDate) {
        return {
          children: (
            <MounthlySeparator
              groupDate={dataObject.groupDate}
              groupRealizedGain={dataObject.groupRealizedGain}
            />
          ),
          props: { colSpan: 8 },
        };
      }

      const isBonds = BONDS.includes(dataObject.command);
      const isDividend = dataObject.command === HistoryType.DIVIDEND;
      const isCapitalReduction =
        dataObject.command === HistoryType.CAPITAL_REDUCTION;
      const isRedemtion = REDEMPTIONS.includes(dataObject.command);
      const useDisplayAs =
        isDividend || isBonds || isRedemtion || isCapitalReduction;

      return (
        <Text family="bold">{useDisplayAs ? dataObject.display_as : text}</Text>
      );
    },
  },
  {
    key: 'lot',
    title: 'Lot Done',
    dataIndex: 'lot',
    className: 'history-lot-done realized',
    render: (lot, dataObject) => {
      const isBonds = BONDS.includes(dataObject.command);
      const isRedemption = REDEMPTIONS.includes(dataObject.command);

      if (isBonds) {
        return renderTableRowContent(
          lot,
          dataObject,
          <Text align="right">
            {dataObject.shares === 0 ? '-' : dataObject.shares}
          </Text>,
        );
      }

      return renderTableRowContent(
        lot,
        dataObject,
        <Text align="right">
          {lot === 0 || isBonds || isRedemption ? '-' : lot}
        </Text>,
      );
    },
  },
  {
    key: 'price',
    title: 'Price',
    dataIndex: 'price',
    className: 'td-mobile history-price',
    render: (text, dataObject) => {
      const isBondsFR = FR_BONDS.includes(dataObject.command);
      const isRedemption = REDEMPTIONS.includes(dataObject.command);
      const isCoupon = COUPONS.includes(dataObject.command);

      if (isCoupon) {
        return renderTableRowContent(
          text,
          dataObject,
          <Text align="right">-</Text>,
        );
      }

      if (isRedemption) {
        return renderTableRowContent(
          text,
          dataObject,
          <Text align="right">100.00%</Text>,
        );
      }

      return renderTableRowContent(
        text,
        dataObject,
        <Text align="right">
          {formatPriceRoundDown(
            isBondsFR ? dataObject?.bonds?.bonds_price : text,
          )}
        </Text>,
      );
    },
  },
  {
    key: 'amount',
    title: 'Amount Done',
    dataIndex: 'amount',
    className: 'td-mobile history-amount-done',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatPrice(text)}</Text>,
      ),
  },
  {
    key: 'fee',
    title: 'Total Fee',
    dataIndex: 'fee',
    render: (text, dataObject) => {
      // isSellFR, isCouponFR, isCouponKAEF01CB, isCouponSBN
      const couponCommands = [
        HistoryType.SELL_FR,
        HistoryType.COUPON_FR,
        HistoryType.COUPON_KAEF01CB,
        HistoryType.COUPON_SBN,
      ];
      const showMinusSign = couponCommands.includes(dataObject.command);

      return renderTableRowContent(
        text,
        dataObject,
        <Text align="right">
          {showMinusSign && text !== 0 && '-'}
          {formatPrice(text, !showMinusSign)}
        </Text>,
      );
    },
  },
  {
    key: 'netamount',
    title: 'Net Amount',
    dataIndex: 'netamount',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatPrice(text)}</Text>,
      ),
  },
  {
    key: 'realized_amount',
    title: 'Realized P&L',
    dataIndex: 'realized_amount',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text
          color={
            dataObject.realized_color_code
              ? dataObject.realized_color_code.light_mode
              : '#333333'
          }
          align="right"
        >
          {getRealizedPnLText(dataObject)}
        </Text>,
      ),
  },
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    className: 'td-mobile history-date normal',
    render: (text, dataObject) =>
      renderTableRowContent(
        text,
        dataObject,
        <Text align="right">{formatDate(text)}</Text>,
      ),
  },
];
